import React, { useId } from 'react';
import { useThemeOptional } from '../context/ThemeContext';

interface LogoProps {
  height?: number;
  textColor?: 'white' | 'dark';
  className?: string;
  style?: React.CSSProperties;
}

/** Full logo: Icon + "CoachDesk365" text — theme-adaptive */
export const Logo: React.FC<LogoProps> = ({ height = 40, textColor = 'white', className, style }) => {
  const iconSize = height * 0.85;
  const fill = textColor === 'white' ? '#ffffff' : 'var(--text, #0f172a)';

  return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', gap: height * 0.25, flexShrink: 0, ...style }}>
      <LogoIcon size={iconSize} variant={textColor === 'white' ? 'homepage' : 'theme'} />
      <span style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif", fontWeight: 800, fontSize: height * 0.5, letterSpacing: -0.5, color: fill }}>CoachDesk365</span>
    </div>
  );
};

interface LogoIconProps {
  size?: number;
  color?: string;
  variant?: 'theme' | 'homepage';
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Theme gradient mappings — each theme gets a unique gradient
 * that matches its color palette (sidebar → primary direction).
 */
const THEME_GRADIENTS: Record<string, [string, string]> = {
  'boho-earth':    ['#C4A87A', '#5C4330'],
  'golden-dusk':   ['#E8B44A', '#7A4504'],
  'rose-quartz':   ['#E88CA0', '#7B2040'],
  'altrosa':       ['#D4A0A0', '#2E3234'],
  'prestige-navy': ['#E8D48C', '#0D1830'],
  'sage-forest':   ['#5ECEA8', '#0F4035'],
  'indigo-bloom':  ['#9B8EF5', '#281E94'],
  'midnight-gold': ['#E8C85C', '#0F172A'],
};

/** Homepage gradient — harmonisch mit dem dunklen Landingpage-Hintergrund */
const HP_GRADIENT: [string, string] = ['#2D5F8A', '#0F2440'];

/** Brand fallback gradient — used outside ThemeProvider (Login, MaintenanceGate) */
const BRAND_GRADIENT: [string, string] = ['#9a826e', '#7c6350'];

const LogoIcon: React.FC<LogoIconProps> = ({ size = 40, color, variant = 'theme', className, style }) => {
  const uid = useId();
  const gradId = `cd-${uid}`;

  const ctx = useThemeOptional();
  const themeId = ctx?.theme ?? null;

  const isHomepage = variant === 'homepage';
  const [stopA, stopB] = isHomepage
    ? HP_GRADIENT
    : themeId
      ? (THEME_GRADIENTS[themeId] || BRAND_GRADIENT)
      : BRAND_GRADIENT;

  const glowId = `cd-glow-${uid}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="24 24 464 464"
      width={size}
      height={size}
      className={className}
      style={{ flexShrink: 0, display: 'block', ...style }}
    >
      <defs>
        <linearGradient id={gradId} x1="88" y1="64" x2="424" y2="448" gradientUnits="userSpaceOnUse">
          <stop stopColor={stopA} />
          <stop offset="1" stopColor={stopB} />
        </linearGradient>
        {isHomepage && (
          <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="blur" />
            <feColorMatrix in="blur" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.45 0" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>
      {/* Rounded square background with gradient + glow on homepage */}
      <g filter={isHomepage ? `url(#${glowId})` : undefined}>
        <rect
          x="64" y="64" width="384" height="384" rx="96"
          fill={color || `url(#${gradId})`}
        />
        {/* C letter — outer arc */}
        <path
          d="M352 136H212C160.072 136 118 178.072 118 230V282C118 333.928 160.072 376 212 376H352"
          stroke="#ffffff"
          strokeWidth="28"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* D letter — vertical + arc */}
        <path
          d="M244 176H292C334.526 176 369 210.474 369 253C369 295.526 334.526 330 292 330H244V176Z"
          stroke="#ffffff"
          strokeWidth="28"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
};

export default LogoIcon;
